import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { FileModel } from "@/models/FileModel.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { SelectItem } from "@/uiKit/atoms/inputs/SelectItem.tsx";
import { ProfilePicture } from "@/uiKit/atoms/ProfilePicture.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { FormSelectInput } from "@/uiKit/molecules/form/FormSelectInput.tsx";

type NurseInfo = {
  id: string;
  firstName: string;
  lastName: string;
  profilePicture?: FileModel;
};

export type NurseSelectFormFieldProps = {
  nurses: NurseInfo[];
  name?: string;
  className?: string;
};

export const NurseSelectFormField: React.FC<NurseSelectFormFieldProps> = ({
  nurses,
  name = "telemonitoringNurseId",
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <FormSelectInput
      id="nurse-select-form-field"
      name={name}
      label={t("nurseInclusion.nurseSelect")}
      fullWidth
      reserveErrorSpace
      className={className}
      disabled={nurses.length < 2}
      selectClasses={{
        select: styles.selected,
      }}
    >
      {nurses.map(nurse => (
        <SelectItem key={nurse.id} value={nurse.id} className={styles.selectItem}>
          <div className={styles.profilePictureContainer}>
            <ProfilePicture
              src={nurse.profilePicture?.url}
              firstName={nurse.firstName}
              lastName={nurse.lastName}
              size="scale"
            />
          </div>
          <Typography>{userFormattedName(nurse.firstName, nurse.lastName)}</Typography>
        </SelectItem>
      ))}
    </FormSelectInput>
  );
};

const makeStyles = (theme: Theme) => ({
  selected: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
    maxHeight: theme.spacing(12),
  }),
  profilePictureContainer: css({
    width: theme.spacing(12),
    height: theme.spacing(12),
  }),
  selectItem: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
  }),
});
